<script setup>
import { defineOptions, defineProps, onMounted } from "vue";
import GenericButton from "./GenericButton.vue";

defineOptions({ name: "GenericModal" });
const props = defineProps({
  textBtn: { type: String, required: true },
  icon: { type: String, required: false },
  title: { type: String, required: false },
  class: { type: String, required: false },
  openOnMount: { type: Boolean, default: false },
  id: { type: String, required: false, default: "dialog" },
});

onMounted(() => {
  if (props.openOnMount) {
    const dialog = document.getElementById(props.id ?? "dialog");
    dialog?.showModal?.();
  }
});
</script>

<template>
  <GenericButton
    variant="outline"
    command="show-modal"
    :commandfor="props.id ?? 'dialog'"
    :class="{
      hidden: openOnMount,
    }"
  >
    {{ textBtn }}
  </GenericButton>

  <el-dialog>
    <dialog
      :id="props.id ?? 'dialog'"
      aria-labelledby="dialog-title"
      class="fixed inset-0 size-auto max-h-screen max-w-none overflow-y-auto bg-transparent backdrop:bg-transparent"
    >
      <el-dialog-backdrop
        class="fixed inset-0 bg-gray-500/75 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in"
      ></el-dialog-backdrop>

      <div
        tabindex="0"
        class="flex min-h-full items-end justify-center p-4 text-center focus:outline-none sm:items-center sm:p-0"
      >
        <el-dialog-panel
          class="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in sm:my-8 data-closed:sm:translate-y-0 data-closed:sm:scale-95 w-fit max-h-[calc(100vh-4rem)] overflow-y-auto"
          :class="props.class"
        >
          <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4 w-full">
            <div class="flex items-center justify-between sticky top-2 z-50">
              <h3
                id="dialog-title"
                class="text-base font-semibold text-gray-900"
              >
                {{ props.title }}
              </h3>
              <button
                v-if="!openOnMount"
                type="button"
                command="close"
                :commandfor="props.id ?? 'dialog'"
                class="bg-white rounded-lg cursor-pointer p-2"
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 15 15"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M6.793 7.5 1.146 1.854l.708-.708L7.5 6.793l5.647-5.647.707.708L8.207 7.5l5.647 5.646-.707.707L7.5 8.208l-5.646 5.647-.708-.708z"
                    fill="#000"
                  />
                </svg>
              </button>
            </div>
            <div class="sm:flex sm:items-start">
              <div
                v-if="props.icon"
                class="mx-auto flex size-12 shrink-0 items-center justify-center rounded-full bg-red-100 sm:mx-0 sm:size-10"
              >
                {{ props.icon }}
              </div>
              <div class="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                <div class="mt-2">
                  <p class="text-sm text-gray-500" v-if="props.description">
                    {{ props.description }}
                  </p>
                  <slot v-else></slot>
                </div>
              </div>
            </div>
          </div>
          <div class="px-4 py-3">
            <slot name="actions"></slot>
          </div>
        </el-dialog-panel>
      </div>
    </dialog>
  </el-dialog>
</template>
